import React, { useState } from 'react';
import { useNotify, useRedirect, useRefresh } from 'react-admin';
import { gql } from 'apollo-boost';
import {
    Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField,
} from '@mui/material';
import { client } from '../helpers/apolloClient';

const GENERATE = gql`
    mutation generatePaymentPeriod($record: GeneratePaymentPeriodInput!) {
        generatePaymentPeriod(record: $record) {
            id month label status rowCount untickableCount
        }
    }
`;

const currentMonth = () => new Date().toISOString().slice(0, 7);

// The month list is only a picker, so this is the one action that actually
// creates a month. Generation is finance-guarded on the server.
const GenerateMonthButton = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const redirect = useRedirect();

    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(currentMonth());
    const [busy, setBusy] = useState(false);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await client.mutate({ mutation: GENERATE, variables: { record: { month } } });
            const period = data.generatePaymentPeriod;
            notify(
                `${period.label} generated with ${period.rowCount} ${period.rowCount === 1 ? 'row' : 'rows'}` +
                (period.untickableCount ? `, ${period.untickableCount} needing attention` : '')
            );
            setOpen(false);
            refresh();
            redirect('show', 'PaymentPeriod', period.id);
        } catch (e) {
            notify(e.message, { type: 'warning' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button size="small" onClick={() => { setMonth(currentMonth()); setOpen(true); }}>
                Generate month
            </Button>
            <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }}>
                <DialogTitle>Generate a payment month</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Builds the month from the standing allowances. Rows start ticked; anyone without an account holder, without a bank account, or with several accounts is flagged until finance resolves it. Changing an allowance later never changes a month already generated.
                    </DialogContentText>
                    <TextField
                        autoFocus margin="dense" type="month" label="Month" fullWidth
                        value={month}
                        onChange={e => setMonth(e.target.value)}
                        helperText="Format YYYY-MM"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button onClick={generate} variant="contained" disabled={busy || !month}>Generate</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default GenerateMonthButton;
